// src/utils/crashlytics.ts

import { analytics, performance } from '../lib/firebase';
import { logEvent } from 'firebase/analytics';
import { trace } from 'firebase/performance';

export interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  screenWidth: number;
  screenHeight: number;
  windowWidth: number;
  windowHeight: number;
  devicePixelRatio: number;
  colorDepth: number;
  timezone: string;
  isOnline: boolean;
  connectionType?: string;
  isMobile: boolean;
  isIOS: boolean;
  isSafari: boolean;
  osVersion?: string;
  browserVersion?: string;
}

export interface ErrorInfo {
  message: string;
  stack?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  filename?: string;
  timestamp: number;
  url: string;
  userId?: string;
  workspaceId?: string;
  boardId?: string;
  componentStack?: string;
  errorBoundary?: boolean;
  deviceInfo: DeviceInfo;
}

class ErrorTrackingService {
  private isInitialized = false;
  private userId?: string;
  private workspaceId?: string;
  private boardId?: string;

  /**
   * Crashlyticsサービスの初期化
   */
  init() {
    if (this.isInitialized) return;

    // グローバルエラーハンドラーの設定
    window.addEventListener('error', this.handleGlobalError.bind(this));
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));

    // リソース読み込みエラーの監視
    window.addEventListener('error', this.handleResourceError.bind(this), true);

    this.isInitialized = true;
  }

  /**
   * ユーザー情報の設定
   */
  setUser(userId: string, additionalInfo?: Record<string, string | undefined>) {
    this.userId = userId;
    
    // Firebase Analyticsにユーザー情報を設定
    try {
      if (analytics) {
        logEvent(analytics, 'user_login', {
          user_id: userId,
          ...additionalInfo
        });
      }
    } catch (error) {
      console.warn('Failed to log user info to Analytics:', error);
    }
  }

  /**
   * セッション情報の設定
   */
  setSessionInfo(workspaceId?: string, boardId?: string) {
    this.workspaceId = workspaceId;
    this.boardId = boardId;
  }

  /**
   * デバイス情報の取得
   */
  private getDeviceInfo(): DeviceInfo {
    const nav = navigator as any;
    const screen = window.screen;
    
    // iOS判定の改善
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // Safari判定
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // OS版本の取得（iOS用）
    let osVersion: string | undefined;
    if (isIOS) {
      const match = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
      if (match) {
        osVersion = `${match[1]}.${match[2]}${match[3] ? '.' + match[3] : ''}`;
      }
    }

    // ブラウザ版本の取得
    let browserVersion: string | undefined;
    if (isSafari) {
      const match = navigator.userAgent.match(/Version\/(\d+\.\d+)/);
      if (match) {
        browserVersion = match[1];
      }
    }

    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenWidth: screen.width,
      screenHeight: screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      colorDepth: screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      isOnline: navigator.onLine,
      connectionType: (nav.connection?.effectiveType) || 'unknown',
      isMobile: window.innerWidth <= 768,
      isIOS,
      isSafari,
      osVersion,
      browserVersion
    };
  }

  /**
   * JavaScriptエラーのハンドリング
   */
  private handleGlobalError(event: ErrorEvent) {
    const errorInfo: ErrorInfo = {
      message: event.message,
      stack: event.error?.stack,
      source: event.error?.constructor?.name || 'GlobalError',
      lineno: event.lineno,
      colno: event.colno,
      filename: event.filename,
      timestamp: Date.now(),
      url: window.location.href,
      userId: this.userId,
      workspaceId: this.workspaceId,
      boardId: this.boardId,
      deviceInfo: this.getDeviceInfo()
    };

    this.logError(errorInfo);
  }

  /**
   * Promise rejectionのハンドリング
   */
  private handleUnhandledRejection(event: PromiseRejectionEvent) {
    const errorInfo: ErrorInfo = {
      message: event.reason?.message || String(event.reason),
      stack: event.reason?.stack,
      source: 'UnhandledPromiseRejection',
      timestamp: Date.now(),
      url: window.location.href,
      userId: this.userId,
      workspaceId: this.workspaceId,
      boardId: this.boardId,
      deviceInfo: this.getDeviceInfo()
    };

    this.logError(errorInfo);
  }

  /**
   * リソース読み込みエラーのハンドリング
   * スクリプト読み込みエラーは重要なので検出する
   */
  private handleResourceError(event: Event) {
    const target = event.target as HTMLElement;
    
    // スクリプト読み込みエラーのみを検出
    if (target && target.tagName === 'SCRIPT') {
      const errorInfo: ErrorInfo = {
        message: `Script load error: ${target.getAttribute('src') || 'unknown'}`,
        source: 'ScriptLoadError',
        timestamp: Date.now(),
        url: window.location.href,
        userId: this.userId,
        workspaceId: this.workspaceId,
        boardId: this.boardId,
        deviceInfo: this.getDeviceInfo()
      };

      this.logError(errorInfo);
    }
    
    // 画像読み込みエラーは各コンポーネントで適切にハンドリングされているため、
    // Crashlyticsへの送信は無効化
    return;
  }

  /**
   * React Error Boundaryエラーのログ
   */
  logReactError(error: Error, errorInfo: { componentStack?: string | null }) {
    const crashInfo: ErrorInfo = {
      message: error.message,
      stack: error.stack,
      source: 'ReactErrorBoundary',
      timestamp: Date.now(),
      url: window.location.href,
      userId: this.userId,
      workspaceId: this.workspaceId,
      boardId: this.boardId,
      componentStack: errorInfo.componentStack || undefined,
      errorBoundary: true,
      deviceInfo: this.getDeviceInfo()
    };

    this.logError(crashInfo);
  }

  /**
   * カスタムエラーのログ
   */
  logCustomError(message: string, details?: Record<string, any>) {
    const errorInfo: ErrorInfo = {
      message,
      source: 'CustomError',
      timestamp: Date.now(),
      url: window.location.href,
      userId: this.userId,
      workspaceId: this.workspaceId,
      boardId: this.boardId,
      deviceInfo: this.getDeviceInfo(),
      ...details
    };

    this.logError(errorInfo);
  }

  /**
   * エラー情報のログ出力
   */
  private async logError(errorInfo: ErrorInfo) {
    // Google Analyticsの通信エラーは除外してコンソール出力を抑制
    const isAnalyticsNetworkError = 
      errorInfo.message === 'Network request failed' && 
      errorInfo.source === 'CustomError' &&
      errorInfo.url?.includes('localhost') &&
      (errorInfo as any).path === '/g/collect';

    // 除外対象以外はコンソールに出力
    if (!isAnalyticsNetworkError) {
      console.error('🚨 Error logged to Crashlytics:', errorInfo);
    }

    // Firebase Analyticsにエラーイベントを送信（Analytics自体のエラーは除外）
    if (!isAnalyticsNetworkError) {
      try {
        if (analytics) {
          logEvent(analytics, 'exception', {
            description: errorInfo.message,
            fatal: false,
            source: errorInfo.source,
            is_ios: errorInfo.deviceInfo.isIOS,
            is_safari: errorInfo.deviceInfo.isSafari,
            os_version: errorInfo.deviceInfo.osVersion,
            browser_version: errorInfo.deviceInfo.browserVersion,
            screen_width: errorInfo.deviceInfo.screenWidth,
            screen_height: errorInfo.deviceInfo.screenHeight,
            window_width: errorInfo.deviceInfo.windowWidth,
            window_height: errorInfo.deviceInfo.windowHeight
          });
        }
      } catch (analyticsError) {
        console.warn('Failed to log error to Analytics:', analyticsError);
      }
    }

    // 開発環境では詳細情報をコンソールに出力
    if (import.meta.env.MODE === 'development') {
      console.group('🔍 Error Details');
      console.groupEnd();
    }
  }

  /**
   * パフォーマンス計測の開始
   */
  async startTrace(traceName: string) {
    try {
      // Firebase Performanceが利用可能かチェック
      if (!performance || typeof trace !== 'function') {
        console.warn(`Firebase Performance not available for trace '${traceName}'`);
        return null;
      }

      const firebaseTrace = trace(performance, traceName);
      if (!firebaseTrace) {
        console.warn(`Failed to create Firebase trace '${traceName}'`);
        return null;
      }

      // trace.start()は非同期処理なので完了を待つ
      await firebaseTrace.start();

      return firebaseTrace;
    } catch (error) {
      console.warn(`Failed to start performance trace '${traceName}':`, error);
      return null;
    }
  }

  /**
   * カスタムメトリクスの記録
   */
  logMetric(metricName: string, value: number, attributes?: Record<string, string | number | boolean>) {
    try {
      if (analytics) {
        logEvent(analytics, 'custom_metric', {
          metric_name: metricName,
          metric_value: value,
          ...attributes
        });
      }
    } catch (error) {
      console.warn('Failed to log metric:', error);
    }
  }

  /**
   * 白い画面検出の特別なログ
   */
  logWhiteScreenDetection() {
    const deviceInfo = this.getDeviceInfo();
    
    // 白い画面が発生した可能性の高い状況を記録
    this.logCustomError('White screen detected', {
      detection_time: Date.now(),
      is_ios: deviceInfo.isIOS,
      is_safari: deviceInfo.isSafari,
      os_version: deviceInfo.osVersion,
      browser_version: deviceInfo.browserVersion,
      memory_info: (performance as any).memory ? {
        used: (performance as any).memory.usedJSHeapSize,
        total: (performance as any).memory.totalJSHeapSize,
        limit: (performance as any).memory.jsHeapSizeLimit
      } : null
    });
  }
}

export const errorTracking = new ErrorTrackingService();
